import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { formatTime, type MeetingConsentKey } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { LevelMeter } from '../../telephony/LevelMeter';
import type { MeetingParticipant, MeetingView } from '../api';
import { initialsOf, useNow } from '../clock';
import { mediaProblemText, type LocalMedia } from '../media';
import { DevicePill, VideoTile } from '../room/parts';
import { HeaderEyebrow, HeaderPill, HeaderTitle, RoomHeader } from '../room/RoomHeader';
import styles from './LobbyView.module.css';

export interface LobbyViewProps {
  meeting: MeetingView;
  media: LocalMedia;
  /** „Jonas Weiler" — the signed-in host. */
  hostName: string;
  onConsent: () => void;
  consentPending: boolean;
  consentError: unknown;
  onAdmit: (participantId: string) => void;
  admittingId: string | null;
  onStart: () => void;
  starting: boolean;
  /**
   * Server message of the last failed start („Die Aufzeichnung konnte nicht gestartet werden. …"): shown until
   * the next attempt, which stays possible.
   */
  startError: string | null;
}

const NOT_CONFIGURED_MESSAGE = 'Videoberatung ist in dieser Umgebung nicht eingerichtet.';

/**
 * Beratung 10 — the host's lobby: camera preview with device pills and level, the consent items the host
 * confirms (the server lists them: four when the meeting records, two without recording), the waiting room
 * with „Zulassen", and „Gespräch starten · Aufzeichnung beginnt" (without recording „Gespräch starten").
 */
export function LobbyView({
  meeting,
  media,
  hostName,
  onConsent,
  consentPending,
  consentError,
  onAdmit,
  admittingId,
  onStart,
  starting,
  startError,
}: LobbyViewProps) {
  const confirmed = meeting.consent.advisorConfirmed;
  const items = meeting.consent.items;
  const [ticked, setTicked] = useState<Partial<Record<MeetingConsentKey, boolean>>>(() =>
    Object.fromEntries(items.map((item) => [item.key, confirmed])),
  );
  const allTicked = items.length > 0 && items.every((item) => ticked[item.key] === true);

  // The last tick sends the host's consent once (the API takes the listed items at once).
  const sentRef = useRef(false);
  useEffect(() => {
    if (!allTicked) sentRef.current = false;
    if (!allTicked || confirmed || sentRef.current) return;
    sentRef.current = true;
    onConsent();
  }, [allTicked, confirmed, onConsent]);

  const guests = meeting.participants.filter((participant) => participant.type === 'guest');
  const notConfigured = !meeting.livekitConfigured;
  const warning = startError ?? (notConfigured ? NOT_CONFIGURED_MESSAGE : null);

  return (
    <div className={styles.page}>
      <RoomHeader variant="lobby" end={meeting.appointment.dealLine}>
        <HeaderEyebrow>DAMOKLES Meetingraum</HeaderEyebrow>
        <HeaderTitle>
          {meeting.appointment.eventTypeName}
          {meeting.appointment.contactName ? ` · ${meeting.appointment.contactName}` : ''}
        </HeaderTitle>
        <HeaderPill>{meeting.appointment.timePill}</HeaderPill>
      </RoomHeader>
      <div className={styles.body}>
        <div className={styles.stageColumn}>
          <VideoTile
            className={styles.preview}
            size="large"
            stream={media.stream}
            mirrored
            name={`${hostName} · Sie`}
            placeholder={media.problem ? mediaProblemText(media.problem) : null}
          />
          <div className={styles.pills}>
            <DevicePill
              kind="Kamera"
              label={media.cameraLabel}
              devices={media.cameras}
              selectedId={media.cameraId}
              onSelect={media.setCameraId}
            />
            <DevicePill
              kind="Mikrofon"
              label={media.microphoneLabel}
              devices={media.microphones}
              selectedId={media.microphoneId}
              onSelect={media.setMicrophoneId}
            />
            <span className={styles.levelPill}>
              Pegel
              <LevelMeter
                local={media.stream}
                remote={null}
                active={media.stream !== null}
                className={styles.meter}
              />
            </span>
          </div>
        </div>

        <div className={styles.side}>
          <section className={styles.card} aria-labelledby="lobby-consent">
            <div id="lobby-consent" className={styles.eyebrow}>
              Einwilligung · Pflicht
            </div>
            <div className={styles.consentList}>
              {items.map((item) => {
                const checked = confirmed || ticked[item.key] === true;
                return (
                  <label
                    key={item.key}
                    className={clsx(styles.consentItem, confirmed && styles.consentLocked)}
                  >
                    <input
                      type="checkbox"
                      className={styles.srOnly}
                      checked={checked}
                      disabled={confirmed || consentPending}
                      onChange={(event) =>
                        setTicked((value) => ({ ...value, [item.key]: event.target.checked }))
                      }
                    />
                    <span className={clsx(styles.box, checked && styles.boxOn)} aria-hidden="true">
                      {checked ? <Check size={11} strokeWidth={2.5} /> : null}
                    </span>
                    {item.label}
                  </label>
                );
              })}
            </div>
            <div className={styles.note}>{meeting.consent.note}</div>
            {consentError !== null ? (
              <div className={styles.inlineError} role="alert">
                {errorMessage(consentError)}{' '}
                <button type="button" className={styles.textButton} onClick={onConsent}>
                  Erneut senden
                </button>
              </div>
            ) : null}
          </section>

          <WaitingRoom meeting={meeting} guests={guests} onAdmit={onAdmit} admittingId={admittingId} />

          {warning ? (
            <Banner tone="warning" className={styles.banner}>
              {warning}
            </Banner>
          ) : null}
          <button
            type="button"
            className={styles.cta}
            disabled={!confirmed || notConfigured || starting}
            aria-busy={starting || undefined}
            onClick={onStart}
          >
            {meeting.recording.enabled ? 'Gespräch starten · Aufzeichnung beginnt' : 'Gespräch starten'}
          </button>
        </div>
      </div>
    </div>
  );
}

function guestLine(participant: MeetingParticipant, now: number): string {
  switch (participant.state) {
    case 'waiting': {
      const since = participant.waitingSince ? Date.parse(participant.waitingSince) : now;
      const minutes = Math.max(1, Math.floor((now - since) / 60_000));
      const consent = participant.consentAt
        ? `Einwilligung ${formatTime(participant.consentAt)} bestätigt · `
        : '';
      return `${consent}wartet seit ${minutes} Min`;
    }
    case 'declined':
      return 'Einwilligung verweigert';
    case 'admitted':
      return 'Zugelassen · tritt gleich bei';
    case 'joined':
      return 'Im Meetingraum';
    case 'left':
      return 'Hat den Meetingraum verlassen';
    case 'invited':
      return 'Eingeladen · noch nicht beigetreten';
  }
}

function WaitingRoom({
  meeting,
  guests,
  onAdmit,
  admittingId,
}: {
  meeting: MeetingView;
  guests: MeetingParticipant[];
  onAdmit: (participantId: string) => void;
  admittingId: string | null;
}) {
  const now = useNow(30_000);
  const { toast } = useToast();
  const guestUrl = meeting.guestUrl;

  async function copyLink() {
    if (!guestUrl) return;
    try {
      await navigator.clipboard.writeText(guestUrl);
      toast({ message: 'Link kopiert' });
    } catch {
      toast({ message: guestUrl });
    }
  }

  return (
    <section
      className={clsx(styles.card, styles.waitingCard)}
      aria-labelledby="lobby-waiting"
      aria-live="polite"
    >
      <div className={styles.waitingHead}>
        <span id="lobby-waiting" className={styles.eyebrow}>
          Warteraum
        </span>
        {meeting.waitingCount > 0 ? (
          <span className={styles.count}>{meeting.waitingCount} wartet</span>
        ) : null}
      </div>
      {guests.map((guest, index) => {
        const waiting = guest.state === 'waiting';
        return (
          <div
            key={guest.id}
            className={clsx(
              styles.guest,
              index === 0 ? styles.guestFirst : null,
              waiting && styles.guestWaiting,
            )}
          >
            <span className={clsx(styles.avatar, waiting && styles.avatarWaiting)} aria-hidden="true">
              {initialsOf(guest.name)}
            </span>
            <span className={styles.guestText}>
              <span className={styles.guestName}>{guest.name}</span>
              <span className={clsx(styles.guestSub, waiting && styles.guestSubWaiting)}>
                {guestLine(guest, now)}
              </span>
            </span>
            {waiting ? (
              <button
                type="button"
                className={styles.admit}
                disabled={admittingId !== null}
                aria-busy={admittingId === guest.id || undefined}
                onClick={() => onAdmit(guest.id)}
              >
                Zulassen
              </button>
            ) : null}
          </div>
        );
      })}
      {guestUrl ? (
        <button
          type="button"
          className={clsx(styles.textButton, styles.copyLink)}
          onClick={() => void copyLink()}
        >
          Link für den Gast kopieren
        </button>
      ) : null}
    </section>
  );
}
